import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { supabase } from "../supabase/supabase";
import { installAxiosTrialExpiredInterceptor } from "../utils/trialExpiredHandler";

// Base API configuration
const API_BASE_URL =
  process.env.REACT_APP_BACKEND_BASE_URL || "http://localhost:3001/api";

// Standard error response interface
interface ApiErrorResponse {
  error_code: string;
  message: string;
  status?: number;
}

// Custom error class for API errors
export class ApiError extends Error {
  public status: number;
  public errorCode: string;
  public message: string;

  constructor(status: number, errorCode: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.errorCode = errorCode;
    this.message = message;
  }
}

// Create axios instance with default configuration
const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 10000, // 10 seconds
    headers: {
      "Content-Type": "application/json",
    },
  });

  // Request interceptor to add auth token
  instance.interceptors.request.use(
    async (config) => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (session?.access_token) {
          config.headers.Authorization = `Bearer ${session.access_token}`;
        }
      } catch (error) {
        console.error("Error getting access token:", error);
      }

      // Don't override Content-Type for FormData - let browser set it with boundary
      if (config.data instanceof FormData) {
        delete config.headers["Content-Type"];
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor to handle errors
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    (error) => {
      if (error.response) {
        // Server responded with error status
        const { status, data } = error.response;

        if (status === 401) {
          console.error("Unauthorized access - token may be invalid");
          // Optionally trigger logout or token refresh
        }

        // Create ApiError with server response data
        const errorData = data as ApiErrorResponse;
        const apiError = new ApiError(
          status,
          errorData.error_code || "HTTP_ERROR",
          errorData.message || `HTTP ${status}: ${error.response.statusText}`
        );
        return Promise.reject(apiError);
      } else if (error.request) {
        // Request was made but no response received
        const apiError = new ApiError(
          0,
          "NETWORK_ERROR",
          "Network error - no response received"
        );
        return Promise.reject(apiError);
      } else {
        // Something else happened
        const apiError = new ApiError(
          0,
          "REQUEST_ERROR",
          error.message || "Request configuration error"
        );
        return Promise.reject(apiError);
      }
    }
  );

  return instance;
};

// Create the main axios instance.
// Wire global trial-expired detection (Phase 3d). Any 403 with body
// `{ error_code: 'trial_expired' }` from any backend surface redirects to
// `/trial-expired`. Installed here (not in App.tsx) so the interceptor sits
// INSIDE the axios response pipeline alongside the existing ApiError mapping.
const apiClient = createAxiosInstance();
installAxiosTrialExpiredInterceptor(apiClient);

// Helper function to make authenticated API calls
export const makeAuthenticatedRequest = async <T = any>(
  endpoint: string,
  config: AxiosRequestConfig = {}
): Promise<T> => {
  try {
    const response = await apiClient.request({
      url: endpoint,
      ...config,
    });

    // Return the data from the response
    return response.data;
  } catch (error) {
    // Re-throw ApiError instances
    if (error instanceof ApiError) {
      throw error;
    }

    // Convert axios errors to ApiError
    if (axios.isAxiosError(error)) {
      const status = error.response?.status || 0;
      const errorData = error.response?.data as ApiErrorResponse;

      throw new ApiError(
        status,
        errorData?.error_code || "HTTP_ERROR",
        errorData?.message || error.message || "An unexpected error occurred"
      );
    }

    // Fallback for other errors
    throw new ApiError(
      0,
      "UNKNOWN_ERROR",
      error instanceof Error ? error.message : "An unknown error occurred"
    );
  }
};

// Helper functions for common HTTP methods
export const apiGet = async <T = any>(endpoint: string): Promise<T> => {
  return makeAuthenticatedRequest<T>(endpoint, { method: "GET" });
};

export const apiPost = async <T = any>(
  endpoint: string,
  data?: any
): Promise<T> => {
  return makeAuthenticatedRequest<T>(endpoint, {
    method: "POST",
    data,
  });
};

export const apiPut = async <T = any>(
  endpoint: string,
  data?: any
): Promise<T> => {
  return makeAuthenticatedRequest<T>(endpoint, {
    method: "PUT",
    data,
  });
};

export const apiPatch = async <T = any>(
  endpoint: string,
  data?: any
): Promise<T> => {
  return makeAuthenticatedRequest<T>(endpoint, {
    method: "PATCH",
    data,
  });
};

export const apiDelete = async <T = any>(endpoint: string): Promise<T> => {
  return makeAuthenticatedRequest<T>(endpoint, { method: "DELETE" });
};

// File upload helper
export const apiUpload = async <T = any>(
  endpoint: string,
  file: File,
  onUploadProgress?: (progressEvent: any) => void
): Promise<T> => {
  const formData = new FormData();
  formData.append("file", file);

  return makeAuthenticatedRequest<T>(endpoint, {
    method: "POST",
    data: formData,
    headers: {
      "Content-Type": "multipart/form-data",
    },
    onUploadProgress,
  });
};

// Error handling utilities
export const isApiError = (error: any): error is ApiError =>
  error instanceof ApiError;

export const getErrorMessage = (error: any): string =>
  isApiError(error)
    ? error.message
    : error?.message || "An unexpected error occurred";

export const isUnauthorizedError = (error: any): boolean =>
  isApiError(error) && error.status === 401;

export const isNotFoundError = (error: any): boolean =>
  isApiError(error) && error.status === 404;

export const isNetworkError = (error: any): boolean =>
  isApiError(error) && error.errorCode === "NETWORK_ERROR";

// Export the axios instance for advanced usage
export { apiClient };

// Export axios for type definitions
export { axios };
